import { Button, Grid, Stack } from '@razz-team/ui-web';
import { HomeSummary } from './HomeSummary';
import { AccountList } from './AccountList';
import { VaultList } from './VaultList';
import { useTranslation } from 'react-i18next';
import { MainLayout, useMainLayoutStyles } from '../../Layout/MainLayout';
import { CardList } from './CardList';
import { useIsCardsAvailable } from '../../../api/customers';
import { ThreeDSList } from './ThreeDSList';
import { useHasRole } from '../../../api/access';
import { DigitalWalletTokenList } from './DigitalWalletTokenList';
import { useBusinessMembersQuery } from '../../../api/members';
import { useMemo } from 'react';
import { useCardListQuery } from '../../../api/cards';
import { useAccountsQuery } from '../../../api/accounts';
import { useVaultsQuery } from '../../../api/vaults';
import { filterHomeEntities } from './utils/filterHomeEntities';
import { LastTransactions } from '../AccountPage/LastTransactions';

export function HomePage() {
  const { t } = useTranslation();
  const { spacing } = useMainLayoutStyles();
  const isAdmin = useHasRole('ADMIN');
  const isViewer = useHasRole('VIEWER');
  const isCardsAvailable = useIsCardsAvailable();
  const membersQuery = useBusinessMembersQuery();
  const cardsQuery = useCardListQuery();
  const accountsQuery = useAccountsQuery();
  const vaultsQuery = useVaultsQuery();

  const { cards, accounts } = useMemo(() => {
    if (!isViewer) {
      return {
        cards: cardsQuery.data,
        accounts: accountsQuery.data,
      };
    }

    return filterHomeEntities({
      accounts: accountsQuery.data ?? [],
      cards: isCardsAvailable ? (cardsQuery.data ?? []) : [],
      members: membersQuery.data?.members ?? [],
    });
  }, [accountsQuery.data, cardsQuery.data, isCardsAvailable, isViewer, membersQuery.data]);

  const isCardsLoading = cardsQuery.isPending || membersQuery.isLoading;
  const cardsError = cardsQuery.error ?? membersQuery.error;

  const isAccountsLoading = accountsQuery.isPending || (isCardsAvailable && isCardsLoading);
  const accountsError = accountsQuery.error;

  return (
    <MainLayout title={t('Home')}>
      {isViewer ? (
        <Grid size={12}>
          <HomeSummary />
        </Grid>
      ) : null}
      <Grid
        size={{
          md: 6,
          xs: 12,
        }}
      >
        <Stack direction="column" spacing={spacing} width="100%">
          {isCardsAvailable ? <DigitalWalletTokenList /> : null}
          <AccountList
            title={t('Business accounts')}
            accounts={accounts}
            error={accountsError}
            isLoading={isAccountsLoading}
            actions={
              isAdmin ? (
                <Button variant="text" color="info" href="/account/accounts/create">
                  {t('Open account')}
                </Button>
              ) : isViewer ? (
                <Button variant="text" color="info" href="/account/accounts">
                  {t('View all')}
                </Button>
              ) : null
            }
          />
          {isViewer ? (
            <VaultList
              title={t('Earning vaults')}
              vaults={vaultsQuery.data}
              error={vaultsQuery.error}
              isLoading={vaultsQuery.isPending}
              actions={
                isAdmin ? (
                  <Button variant="text" color="info" href="/account/vaults/create">
                    {t('Open vault')}
                  </Button>
                ) : isViewer ? (
                  <Button variant="text" color="info" href="/account/vaults">
                    {t('View all')}
                  </Button>
                ) : null
              }
            />
          ) : null}
          {isCardsAvailable ? (
            <CardList
              title={t('Business cards')}
              actions={
                isAdmin ? (
                  <Button variant="text" color="info" href="/account/cards/create">
                    {t('Open card')}
                  </Button>
                ) : isViewer ? (
                  <Button variant="text" color="info" href="/account/cards">
                    {t('View all')}
                  </Button>
                ) : null
              }
              cards={cards}
              error={cardsError}
              isLoading={isCardsLoading}
            />
          ) : null}
        </Stack>
      </Grid>
      <Grid
        size={{
          md: 6,
          xs: 12,
        }}
      >
        <Stack direction="column" spacing={spacing} width="100%">
          {isCardsAvailable ? <ThreeDSList /> : null}
          <LastTransactions />
        </Stack>
      </Grid>
    </MainLayout>
  );
}
